import getLuxLoyaltyPointBurnCalculationKey from 'checkout/lib/utils/luxLoyalty/getLuxLoyaltyPointBurnCalculationKey'
import { EmptyArray, sortBy, sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import getPayableTotal from '../payment/getPayableTotal'
import getAllItemViews from '../view/getAllItemViews'
import getCheckoutLuxLoyaltyPointsBurnCalculationRequests from './getCheckoutLuxLoyaltyPointsBurnCalculationRequests'
import getCreditPayAmount from '../payment/getCreditPayAmount'

const LOADING_VALUE: App.StatefulData<App.Checkout.LuxLoyaltyPointBurnCalculationView> = {
  fetching: true,
}
const EMPTY_VALUE: App.StatefulData<App.Checkout.LuxLoyaltyPointBurnCalculationView> = {
  fetching: false,
  data: {
    items: EmptyArray,
    itemsTotalPriceInCurrency: 0,
    itemsTotalPriceInPoints: 0,
    maxBurnablePoints: 0,
    minBurnablePoints: 0,
  },
}

/**
 * View items are sorted by the highest point-to-currency ratio in desc order.
 */
const getCheckoutLuxLoyaltyPointBurnCalculationView = createSelector(
  (state: App.State) => state.luxLoyalty.account,
  (state: App.State) => getCheckoutLuxLoyaltyPointsBurnCalculationRequests(state),
  (state: App.State) => state.luxLoyalty.pointsBurnCalculations,
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => getCreditPayAmount(state),
  (
    luxLoyaltyAccount,
    pointsBurnCalculationRequests,
    pointsBurnCalculations,
    allItemViews,
    payableTotal,
    creditPaymentAmount,
  ): App.StatefulData<App.Checkout.LuxLoyaltyPointBurnCalculationView> => {
    if (luxLoyaltyAccount.initial || luxLoyaltyAccount.fetching) return LOADING_VALUE
    if (!luxLoyaltyAccount.data) return EMPTY_VALUE
    if (!allItemViews.hasRequiredData) return LOADING_VALUE
    if (!pointsBurnCalculationRequests.length) return EMPTY_VALUE

    const calculationKey = getLuxLoyaltyPointBurnCalculationKey(pointsBurnCalculationRequests)
    const pointsBurnCalculation = pointsBurnCalculations[calculationKey]
    if (!pointsBurnCalculation?.data) return LOADING_VALUE

    const items = pointsBurnCalculation.data.items.reduce<Array<App.Checkout.LuxLoyaltyPointBurnCalculationViewItem>>((acc, calcItem) => {
      const itemView = allItemViews.data.get(calcItem.itemId)?.data
      if (itemView && calcItem.priceInPoints) {
        acc.push({
          itemView,
          priceInCurrency: calcItem.priceInCurrency,
          priceInPoints: calcItem.priceInPoints,
        })
      }
      return acc
    }, [])

    const itemsTotalPriceInCurrency = sum(items, (item) => item.priceInCurrency)
    const itemsTotalPriceInPoints = Math.round(sum(items, (item) => item.priceInPoints))

    const payable = payableTotal - creditPaymentAmount
    const payableInPoints = (payable * itemsTotalPriceInPoints) / itemsTotalPriceInCurrency

    const maxBurnablePoints = Math.round(Math.min(
      luxLoyaltyAccount.data.points,
      payableInPoints,
    ))

    return {
      fetching: false,
      data: {
        items: sortBy(
          items,
          // sort by point-to-currency ratio
          (i) => i.priceInPoints / i.priceInCurrency,
          'desc',
        ),
        itemsTotalPriceInCurrency,
        itemsTotalPriceInPoints,
        maxBurnablePoints,
        minBurnablePoints: pointsBurnCalculation.data.minBurnablePoints,
      },
    }
  },
)

export default getCheckoutLuxLoyaltyPointBurnCalculationView
